import JsSIP from 'jssip';
import * as stateActions from './actions/stateActions';
import * as requestActions from './actions/requestActions';
import * as sessionStates from './sessionStates';
import Logger from './logger';
import { debugSipMessage, logSipEvent } from './logger';
import { initAudio, setAudioOutputDevice, getGlobalAudioElement } from './utils/audioUtils';
// import { setupPeerConnectionDebug } from './utils/peerConnectionUtils';
import { collectSessionStreams, collectTracks, removeTracksFromStreams, buildAudioOnlyStream, releaseStream } from './utils/streamUtils';
import { createAvatarVideoTrack } from './utils/avatarUtils';
import { checkConnectivity } from './utils/connectivityUtils';
import { setupSessionHandlers } from './utils/sessionHandlers';
import { setupPeerConnectionHandlers } from './utils/peerConnectionHandlers';
import { initializeSession, setupEarlyLocalStreamCapture, setupAddStreamHandler } from './utils/sessionSetup';

JsSIP.debug.enable('JsSIP:*');

const logger = new Logger('SipCaller');

let store;

export default class SipCaller {
	/**
	 * @param  {Object} data
	 * @param  {Object} data.store - The Redux store.
	 */
	static init(data) {
		store = data.store;
	}

	constructor() {
		logger.debug('constructor()');
		this._ua = null;
		this._reconnectAttempts = 0;
		this._maxReconnectAttempts = 5;
		this._reconnectDelay = 2000; // Start with 2 seconds
		this._localStream = null;
		this._hasAudio = false;
		this._hasVideo = false;
		this._videoStreamsByTrackId = new Map();
		
		// Initialize avatar canvas refs
		this._avatarCanvasRef = { current: null };
		this._avatarRenderIntervalRef = { current: null };

		// Initialize audio
		this._audioElement = initAudio();

		this._init();
	}

	_init() {
		logger.debug('_init()');
		try {
			const { autoRegister } = store.getState().user;
			if (autoRegister)
				this.register();
		} catch (error) {
			logger.error('_init() failed:', error);
			store.dispatch(requestActions.notify({
				type: 'error',
				text: `Initialization failed: ${error.message}`
			}));
		}
	}

	register() {
		logger.debug('register()');

		try {
			const {
				displayName,
				sipUri,
				password,
				outboundProxy
			} = store.getState().user;

			if (!sipUri || !password || !outboundProxy) {
				throw new Error('Missing required SIP configuration');
			}

			store.dispatch(stateActions.setRegisterInProgress());

			// Configure socket
			const socket = new JsSIP.WebSocketInterface(outboundProxy);

			socket.onconnect = () => logger.debug('WebSocket connected');
			socket.ondisconnect = () => logger.debug('WebSocket disconnected');
			socket.onmessage = (message) => {
				const isSipMessage = message.data && (
					message.data.includes('SIP/2.0') ||
					message.data.match(/^(INVITE|REGISTER|BYE|CANCEL|ACK|OPTIONS|MESSAGE)/)
				);

				if (isSipMessage) {
					if (message.data.startsWith('SIP/2.0')) {
						debugSipMessage('received', message.data);
					} else {
						debugSipMessage('sent', message.data);
					}
				}
			};

			// Extract domain for registrar_server
			let registrarDomain;
			try {
				// Extract domain from SIP URI or WebSocket URL
				if (sipUri.includes('@')) {
					// Extract from SIP URI (sip:user@domain.com)
					registrarDomain = sipUri.split('@')[1].split('>')[0].split(':')[0];
				} else if (outboundProxy.includes('//')) {
					// Extract from WebSocket URL (wss://domain.com/path)
					registrarDomain = outboundProxy.split('//')[1].split('/')[0].split(':')[0];
				} else {
					// Fallback to the host part of the outbound proxy
					registrarDomain = outboundProxy;
				}
			} catch (error) {
				logger.error('Error extracting domain from URIs:', error);
				registrarDomain = 'urgentnet.works'; // Fallback domain
			}

			// JsSIP configuration
			const configuration = {
				sockets: [socket],
				uri: sipUri,
				password: password,
				display_name: displayName,
				register: true,
				register_expires: 600,
				session_timers: false,
				connection_recovery_min_interval: this._reconnectDelay,
				connection_recovery_max_interval: this._reconnectDelay * 10,
				registrar_server: `sip:${registrarDomain}`,
				ice_servers: [
					{ urls: "stun:urgent-route.com:3478" },
					{
						urls: "turn:urgent-route.com:3478",
						username: "tkabe",
						credential: "tkabe"
					}
				]
			};

			// Create JsSIP User Agent
			this._ua = new JsSIP.UA(configuration);

			// Connection events
			this._ua.on('connecting', () => {
				logger.debug('Connecting to WebSocket');
				this._handleTransportStateChange('connecting');
			});

			this._ua.on('connected', () => {
				logger.debug('WebSocket connected');
				this._reconnectAttempts = 0; // Reset reconnection attempts on successful connection
				this._handleTransportStateChange('connected');
				store.dispatch(requestActions.notify({
					type: 'success',
					text: 'WebSocket connection established'
				}));
			});

			this._ua.on('disconnected', (data) => {
				logger.debug('WebSocket disconnected', data);
				this._handleTransportStateChange('disconnected');
				this._handleWebSocketDisconnect(data.error, data.code, data.reason);
			});

			// Registration events
			this._ua.on('registered', (data) => {
				logSipEvent('registered', data);
				logger.debug('Registered');
				store.dispatch(requestActions.notify({
					type: 'success',
					text: 'Successfully registered.'
				}));
				store.dispatch(stateActions.setRegistrationMessage({ registrationMessage: 'Success' }));
				store.dispatch(stateActions.setRegistered({ registered: true }));
			});

			this._ua.on('unregistered', () => {
				logger.debug('Unregistered');
				store.dispatch(requestActions.notify({
					type: 'warning',
					text: 'SIP registration expired or failed'
				}));
				store.dispatch(stateActions.setRegistered({ registered: false }));
			});

			this._ua.on('registrationFailed', (data) => {
				logSipEvent('registrationFailed', data);
				this._handleRegistrationError(data.cause || new Error('Registration failed'));
			});

			// Incoming call event
			this._ua.on('newRTCSession', (data) => {
				logSipEvent('newRTCSession', data);

				// Enhanced logs for the session
				const session = data.session;
				if (session.request) {
					console.group('SESSION REQUEST DETAILS');
					console.log('Method:', session.request.method);
					console.log('Call-ID:', session.request.call_id);

					// Log the SDP from the initial request
					if (session.request.body) {
						console.group('Request SDP:');
						console.log(session.request.body);
						console.groupEnd();
					}
					console.groupEnd();
				}

				if (data.originator === 'remote') {
					try {
						const session = data.session;
						logger.debug('Incoming call', session);

						// Get caller ID
						const displayName = session.remote_identity.display_name || session.remote_identity.uri.user;

						store.dispatch(requestActions.notify({
							text: `Incoming call from: ${displayName}`
						}));

						this._handleSession(session, sessionStates.INCOMING);
					} catch (error) {
						logger.error('newRTCSession handler failed:', error);
						store.dispatch(requestActions.notify({
							type: 'error',
							text: `Failed to handle incoming call: ${error.message}`
						}));
					}
				}
			});

			// Start the User Agent
			this._ua.start();
		} catch (error) {
			this._handleRegistrationError(error);
		}
	}

	_handleWebSocketDisconnect(error, code, reason) {
		logger.error(`WebSocket disconnected - Code: ${code}, Reason: ${reason}`, error);

		if (code === 1006) {
			store.dispatch(requestActions.notify({
				type: 'error',
				text: 'WebSocket connection failed unexpectedly. Attempting to reconnect...'
			}));

			this._attemptReconnection();
		} else {
			store.dispatch(requestActions.notify({
				type: 'error',
				text: `WebSocket disconnected`
			}));
		}
	}

	_applySavedMuteState(session, providedStream = null) {
		try {
			const user = store.getState().user || {};
			const audioEnabled = user.audioEnabled !== false; // default true
			const videoEnabled = user.videoEnabled === true;  // default false

			// Get the stream from provided parameter, or from JsSIP's connection
			let localStream = providedStream;
			if (!localStream && session.connection && session.connection.getLocalStreams) {
				const streams = session.connection.getLocalStreams();
				if (streams.length > 0) {
					localStream = streams[0];
				}
			}

			if (!localStream) {
				logger.debug('No local stream found in JsSIP session');
				return;
			}

			logger.debug('Applying saved mute state to JsSIP stream:', {
				audioEnabled,
				videoEnabled
			});

			// Apply audio mute state
			const audioTracks = localStream.getAudioTracks();
			audioTracks.forEach(track => {
				track.enabled = audioEnabled;
			});

			// Apply video mute state  
			const videoTracks = localStream.getVideoTracks();
			if (videoTracks.length > 0) {
				if (!videoEnabled) {
					// If video is disabled, replace camera tracks with avatar immediately
					console.log('Video disabled in Redux - replacing camera tracks with avatar');
					this._disableLocalVideo(session, localStream).catch(err => {
						console.error('Failed to disable video in _applySavedMuteState:', err);
						// Fallback: just disable the tracks
						videoTracks.forEach(track => {
							track.enabled = false;
						});
					});
				} else {
					// Video is enabled - ensure tracks are enabled
					videoTracks.forEach(track => {
						track.enabled = true;
					});
				}
			}

			// Also apply to JsSIP session
			// Audio can be managed by JsSIP mute/unmute
			if (!audioEnabled) {
				session.mute({ audio: true });
			} else {
				session.unmute({ audio: true });
			}
			
			// IMPORTANT: Do NOT call JsSIP mute/unmute for video.
			// Video is managed by track replacement (camera <-> avatar).
  

			logger.debug('Successfully applied saved mute state to JsSIP stream');
		} catch (error) {
			logger.error('Failed to apply saved mute state to JsSIP stream:', error);
		}
	}

	_handleTransportStateChange(newState) {
		logger.debug(`Transport state changed to: ${newState}`);

		switch (newState) {
			case 'connecting':
				store.dispatch(stateActions.setRegistrationMessage({
					registrationMessage: 'Connecting...'
				}));
				break;
			case 'connected':
				store.dispatch(stateActions.setRegistrationMessage({
					registrationMessage: 'Connected'
				}));
				break;
			case 'disconnected':
				store.dispatch(stateActions.setRegistrationMessage({
					registrationMessage: 'Disconnected'
				}));
				store.dispatch(stateActions.setRegistered({ registered: false }));
				break;
			case 'failed':
				store.dispatch(stateActions.setRegistrationMessage({
					registrationMessage: 'Connection Failed'
				}));
				store.dispatch(stateActions.setRegistered({ registered: false }));
				break;
			default:
				break;
		}
	}

	_attemptReconnection() {
		if (this._reconnectAttempts >= this._maxReconnectAttempts) {
			logger.error('Max reconnection attempts reached');
			store.dispatch(requestActions.notify({
				type: 'error',
				text: 'Failed to reconnect after multiple attempts. Please check your connection and try again.'
			}));
			store.dispatch(stateActions.setRegistered({ registered: false }));
			return;
		}

		this._reconnectAttempts++;
		const delay = this._reconnectDelay * Math.pow(2, this._reconnectAttempts - 1); // Exponential backoff

		logger.debug(`Attempting reconnection ${this._reconnectAttempts} of ${this._maxReconnectAttempts} in ${delay}ms`);

		setTimeout(() => {
			if (this._ua) {
				try {
					// JsSIP handles reconnection automatically with its connection_recovery settings
					// But we can also manually reconnect if needed
					if (this._ua.isConnected() === false) {
						this._ua.reconnect();
					}
				} catch (error) {
					logger.error('Reconnection attempt failed:', error);
					this._attemptReconnection(); // Try again if this attempt fails
				}
			} else {
				this.register(); // If UA is null, try full registration
			}
		}, delay);
	}

	_handleRegistrationError(error) {
		logger.error('Registration failed:', error);
		store.dispatch(requestActions.notify({
			type: 'error',
			text: `Registration failed: ${error.message || error}`
		}));
		store.dispatch(stateActions.setRegistrationMessage({
			registrationMessage: 'Failed'
		}));
		store.dispatch(stateActions.setRegistered({ registered: false }));
	}

	unRegister() {
		logger.debug('unRegister()');
		try {
			if (this._ua) {
				this._ua.unregister();
				this._ua.stop();
				store.dispatch(stateActions.setRegistered({ registered: false }));
			}
		} catch (error) {
			logger.error('unRegister() failed:', error);
			store.dispatch(requestActions.notify({
				type: 'error',
				text: `Unregister failed: ${error.message}`
			}));
		}
	}

	_handleSession(session, direction) {
		logger.debug('_handleSession()', session);

		try {
			// Initialize session and get callId
			const callId = initializeSession(session, direction, store);

			// Ensure we have an audio element for playback
			const audioElement = getGlobalAudioElement() || initAudio();

			// Setup early local stream capture for outgoing calls
			setupEarlyLocalStreamCapture(session, direction, store, logger, this._applySavedMuteState.bind(this));

			// Setup addstream event handler
			setupAddStreamHandler(session, store, logger, audioElement);

			// Setup initial peer connection handler for mute state
			// setupInitialPeerConnectionHandler(session, store, logger, this._applySavedMuteState.bind(this));

			// Setup all session event handlers
			setupSessionHandlers(
				session,
				callId,
				direction,
				store,
				logger,
				this._stopAllCameraResources.bind(this),
				this._applySavedMuteState.bind(this),
				() => this._videoStreamsByTrackId.clear()
			);

			// Setup peer connection handlers
			setupPeerConnectionHandlers(
				session,
				callId,
				store,
				logger,
				audioElement,
				this._applySavedMuteState.bind(this),
				this._handleLocalTrackAdded.bind(this),
				this._captureLocalStreamDirectly.bind(this)
			);


			// Add session to store
			store.dispatch(stateActions.addSession({ sipSession: session, direction }));

			// Set initial session state (it will be updated as the call progresses)
			store.dispatch(stateActions.setSessionState({
				sipSession: session,
				sessionState: sessionStates.NEW
			}));

			// Ensure camera is OFF by default at call start
			const { videoEnabled } = store.getState().user;
			if (videoEnabled) {
				// If video is enabled, ensure it stays enabled (user preference)
				// But we don't force it ON if it's already OFF
				console.log('Video is enabled in Redux state at call start');
			} else {
				// Video is disabled - ensure it stays disabled
				console.log('Video is disabled in Redux state at call start - camera will be off');
			}

		} catch (error) {
			logger.error('_handleSession() failed:', error);
			store.dispatch(requestActions.notify({
				type: 'error',
				text: `Failed to handle session: ${error.message}`
			}));
		}
	}

	async _buildInitialStreamWithAvatar() {
		const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
		const audioTrack = audioStream.getAudioTracks()[0];
	  
		const avatarTrack = await this._createAvatarVideoTrack( 1280, 720 ); // canvas track (no camera)
	  
		const initial = new MediaStream();
		if (audioTrack) initial.addTrack(audioTrack);
		if (avatarTrack) initial.addTrack(avatarTrack);
	  
		return initial;
	}

	async accept(sipSession) {
		logger.debug('accept()', sipSession, this._hasAudio, this._hasVideo);
		try {
			// Check if the session is still valid
			if (!sipSession) {
				throw new Error('Invalid session: session is null or undefined');
			}

			// Check session state
			const callId = sipSession.callId || (sipSession.request && sipSession.request.callId);
			if (!callId) {
				throw new Error('Invalid session: cannot determine callId');
			}

			// Log session state for debugging
			const sessionData = store.getState().sessions[callId];
			if (sessionData) {
				logger.debug(`Accepting call in state: ${sessionData.sessionState}, direction: ${sessionData.direction}`);
			} else {
				logger.warn('Session not found in Redux store');
			}

			// Set this as the current session
			store.dispatch(stateActions.setCurrentSession({
				currentSession: callId
			}));

			const { videoEnabled } = store.getState().user;

			// Check if the incoming call has video
			let incomingHasVideo = false;
			if (sipSession.request && sipSession.request.body) {
				const sdp = sipSession.request.body;
				incomingHasVideo = sdp.includes('m=video');

				if (incomingHasVideo) {
					logger.debug('Incoming call has video in SDP');
					console.log('Incoming call has video in SDP');
				}
			}

			const initialStream = await this._buildInitialStreamWithAvatar();

			// JsSIP options for answer
			// Negotiate video support in SDP (to receive remote video) but tracks will be disabled initially
			// Video tracks will be enabled when user clicks camera button
			// Note: mediaConstraints must be true to negotiate in SDP, but since we provide mediaStream
			const options = {
				mediaStream: initialStream,
				mediaConstraints: { audio: true, video: true }, // Must be true for SDP negotiation, but mediaStream prevents getUserMedia
				rtcOfferConstraints: { offerToReceiveAudio: 1, offerToReceiveVideo: 1 },
				pcConfig: {
					rtcpMuxPolicy: 'require',
					iceServers: [
						{ urls: "stun:urgent-route.com:3478" },
						{
							urls: "turn:urgent-route.com:3478",
							username: "tkabe",
							credential: "tkabe"
						}
					]
				}
			};

			logger.debug('Accepting incoming call with options:', options);
			console.log('Accepting incoming call with options:', options);

			// Update session state to ESTABLISHING
			store.dispatch(stateActions.setSessionState({
				sipSession: sipSession,
				sessionState: sessionStates.ESTABLISHING
			}));

			// Answer the call
			sipSession.answer(options);

			logger.debug('Call acceptance initiated successfully');

			// Show notification to user
			store.dispatch(requestActions.notify({
				type: 'success',
				text: 'Accepting call...',
				timeout: 3000
			}));

			// Notify user about video call status
			if (incomingHasVideo) {
				// Notify user that this is a video call
				if (videoEnabled) {
					store.dispatch(requestActions.notify({
						type: 'info',
						text: 'Video call detected - enabling camera',
						timeout: 3000
					}));
				} else {
					store.dispatch(requestActions.notify({
						type: 'warning',
						text: 'Video call detected - you will see remote video but not send video',
						timeout: 3000
					}));
				}
			}

			// Set up a timeout to check if the call was established
			setTimeout(() => {
				const updatedSessionData = store.getState().sessions[callId];
				if (updatedSessionData && updatedSessionData.sessionState !== sessionStates.ESTABLISHED) {
					logger.warn('Call not established after timeout, checking connection');

					// Check if we have a connection
					if (sipSession.connection) {
						const iceState = sipSession.connection.iceConnectionState;
						const connState = sipSession.connection.connectionState;

						logger.debug(`Connection state check - ICE: ${iceState}, Connection: ${connState}`);

						if (iceState === 'failed' || connState === 'failed') {
							logger.error('WebRTC connection failed during call setup');
							store.dispatch(requestActions.notify({
								type: 'error',
								text: 'Connection failed. Please check your network and try again.',
								timeout: 5000
							}));
						} else if (iceState === 'checking' || connState === 'connecting') {
							logger.warn('Connection still being established');
							store.dispatch(requestActions.notify({
								type: 'warning',
								text: 'Still connecting... Please wait.',
								timeout: 3000
							}));
						}
					}
				}
			}, 10000); // Check after 10 seconds
		} catch (error) {
			logger.error('accept() failed:', error);
			store.dispatch(requestActions.notify({
				type: 'error',
				text: `Failed to accept call: ${error.message}`
			}));

			// If there was an error, try to terminate the session
			try {
				if (sipSession) {
					sipSession.terminate();
				}
			} catch (terminateError) {
				logger.error('Failed to terminate session after accept error:', terminateError);
			}
		}
	}

	terminate(sipSession) {
		logger.debug('terminate()', sipSession);
		try {
			// STOP CAMERA IMMEDIATELY (don’t wait for 'ended')
			this._stopAllCameraResources(sipSession);
	
			sipSession.terminate();
		} catch (error) {
			logger.error('terminate() failed:', error);
			this._stopAllCameraResources(sipSession);	
			store.dispatch(requestActions.notify({
				type: 'error',
				text: `Failed to terminate call: ${error.message}`
			}));
		}
	}

	async invite(sipUri) {
		logger.debug('invite()', sipUri, this._hasAudio, this._hasVideo, this._localStream);

		try {
			if (!this._ua) {
				throw new Error('User agent not initialized');
			}

			const initialStream = await this._buildInitialStreamWithAvatar();

			// Negotiate video support in SDP (to receive remote video) but tracks will be disabled initially
			// Video tracks will be enabled when user clicks camera button
			// Note: mediaConstraints must be true to negotiate in SDP, but since we provide mediaStream
			const options = {
				mediaStream: initialStream,
				mediaConstraints: { audio: true, video: true }, // Must be true for SDP negotiation, but mediaStream prevents getUserMedia
				rtcOfferConstraints: { offerToReceiveAudio: 1, offerToReceiveVideo: 1 },
				pcConfig: {
					rtcpMuxPolicy: 'require',
					iceServers: [
						{ urls: "stun:urgent-route.com:3478" },
						{
							urls: "turn:urgent-route.com:3478",
							username: "tkabe",
							credential: "tkabe"
						}
					]
				},
				extraHeaders: ['X-App-Name: SIP Tester']
			};

			console.log('Making outgoing call to:', sipUri, options);
			// Make the call
			const session = this._ua.call(sipUri, options);

			// Handle the session first to set up callId BEFORE setting up event handlers
			// This ensures we don't miss any events due to race conditions
			this._handleSession(session, sessionStates.OUTGOING);

			// Now we can use the callId we set in _handleSession
			store.dispatch(
				stateActions.setCurrentSession({
					currentSession: session.callId
				})
			);

			// Enhanced error handling for outgoing calls - set up AFTER _handleSession
			session.on('failed', (data) => {
				console.log('CALL FAILED', data);

				let errorMessage = 'Call failed';

				// Extract more specific error information from the event
				if (data && data.cause) {
					switch (data.cause) {
						case 'Rejected':
						case 'Busy':
							errorMessage = `Call rejected: ${sipUri} is busy or unavailable`;
							break;
						case 'Canceled':
							errorMessage = 'Call was canceled';
							break;
						case 'Not Found':
							errorMessage = `Destination not found: ${sipUri}`;
							break;
						case 'Unavailable':
							errorMessage = `Destination unavailable: ${sipUri}`;
							break;
						case 'WebRTC Error':
							errorMessage = 'WebRTC connection failed. Please check your media devices and try again.';
							break;
						case 'Connection Error':
						case 'Request Timeout':
							errorMessage = 'Network connection error. Please check your internet connection.';
							break;
						case 'Authentication Error':
							errorMessage = 'SIP authentication failed. Please check your credentials.';
							break;
						default:
							errorMessage = `Call failed: ${data.cause}`;
					}
				}

				// Log additional diagnostics to help debugging
				if (data && data.message) {
					console.log(`SIP failure message: ${data.message}`);
				}

				// Log WebRTC connection state if available
				if (session.connection) {
					console.log(`Connection state at failure: ${session.connection.connectionState}`);
					console.log(`ICE connection state: ${session.connection.iceConnectionState}`);
					console.log(`Signaling state: ${session.connection.signalingState}`);
				}

				// Show detailed error message to user
				store.dispatch(requestActions.notify({
					type: 'error',
					text: errorMessage,
					timeout: 6000 // Longer timeout for error messages
				}));
			});

			// More detailed error logging for debugging
			session.on('iceconnectionstatechange', () => {
				if (session.connection) {
					const iceState = session.connection.iceConnectionState;
					console.log(`ICE connection state changed: ${iceState}`);

					if (iceState === 'failed') {
						console.error('ICE connection failed - media connection problem');

						store.dispatch(requestActions.notify({
							type: 'error',
							text: 'Media connection failed. Your network may be blocking WebRTC or STUN/TURN servers.',
							timeout: 6000
						}));
					} else if (iceState === 'disconnected') {
						console.warn('ICE connection disconnected - possible network issue');
					} else if (iceState === 'connected' || iceState === 'completed') {
						console.log('ICE connection established successfully');
					}
				}
			});

			// Log peer connection errors
			if (session.connection) {
				session.connection.addEventListener('connectionstatechange', () => {
					console.log(`Connection state changed: ${session.connection.connectionState}`);

					if (session.connection.connectionState === 'failed') {
						store.dispatch(requestActions.notify({
							type: 'error',
							text: 'WebRTC connection failed. Check your network or firewall settings.',
							timeout: 6000
						}));
					}
				});
			}

			// Log session events for detailed debugging
			session.on('connecting', () => console.log('CALL CONNECTING'));
			session.on('progress', (data) => console.log('CALL PROGRESS', data));
			session.on('accepted', (data) => console.log('CALL ACCEPTED', data));
			session.on('confirmed', () => console.log('CALL CONFIRMED'));
			session.on('ended', (data) => console.log('CALL ENDED', data));
		} catch (error) {
			logger.error('invite() failed:', error);

			// Provide more specific error messages based on the error type
			let errorMessage = 'Failed to make call';

			if (error.name === 'NotAllowedError' || error.name === 'NotFoundError') {
				errorMessage = 'Microphone access denied. Please allow microphone access in your browser.';
			} else if (error.name === 'NotReadableError') {
				errorMessage = 'Microphone is already in use by another application.';
			} else if (error.message && error.message.includes('getUserMedia')) {
				errorMessage = 'Failed to access microphone. Please check your device settings.';
			} else if (error.message) {
				errorMessage = `Failed to make call: ${error.message}`;
			}

			store.dispatch(requestActions.notify({
				type: 'error',
				text: errorMessage,
				timeout: 6000
			}));
			throw error; // Re-throw so the Dialer component can handle it
		}
	}

	refer(sipSession, sipUri) {
		logger.debug('refer()', sipSession, sipUri);
		try {
			if (!sipSession || !sipUri) {
				throw new Error('Invalid session or URI for transfer');
			}

			// Perform the transfer (REFER)
			sipSession.refer(sipUri);
		} catch (error) {
			logger.error('refer() failed:', error);
			store.dispatch(requestActions.notify({
				type: 'error',
				text: `Failed to refer call: ${error.message}`
			}));
		}
	}

	sendDTMF(sipSession, tone) {
		logger.debug('sendDTMF()', sipSession, tone);
		try {
			if (sipSession && sipSession.sendDTMF) {
				sipSession.sendDTMF(tone);
			} else if (sipSession) {
				// If sendDTMF method isn't available, try to set it up now
				const localStream = sipSession.connection.getLocalStreams()[0];
				if (localStream && localStream.getAudioTracks().length > 0) {
					const dtmfSender = sipSession.connection.createDTMFSender(localStream.getAudioTracks()[0]);
					sipSession.sendDTMF = function (t) {
						dtmfSender.insertDTMF(t);
					};
					sipSession.sendDTMF(tone);
				} else {
					throw new Error('No local audio track available for DTMF');
				}
			} else {
				throw new Error('No active session for DTMF');
			}
		} catch (error) {
			logger.error('sendDTMF() failed:', error);
			store.dispatch(requestActions.notify({
				type: 'error',
				text: `Failed to send DTMF: ${error.message}`
			}));
		}
	}

	async toggleMyMedia(session, type) {
		try {
			const { audioEnabled, videoEnabled } = store.getState().user;
			const shouldMute = type === 'audio' ? audioEnabled : videoEnabled;
			// Use the callId we've already set on the session object
			const callId = session.callId;
			if (!callId) {
				logger.error('Cannot find callId in session object');
				return;
			}

			let localStream = store.getState().sessions[callId].localStream;

			if (!localStream && session.connection) {
				logger.debug('No local stream found in Redux store, trying to get from RTCPeerConnection');

				// Try different methods to get the local stream from the peer connection
				if (session.connection.getLocalStreams && session.connection.getLocalStreams().length > 0) {
					localStream = session.connection.getLocalStreams()[0];
					logger.debug('Got local stream from connection.getLocalStreams()');

					// Update the Redux store with this stream for future use
					store.dispatch(stateActions.addLocalStream({
						sipSession: session,
						localStream
					}));
				} else if (session.connection.getSenders) {
					const senders = session.connection.getSenders();
					if (senders && senders.length > 0) {
						const tracks = senders.map(sender => sender.track).filter(track => track !== null);
						if (tracks.length > 0) {
							localStream = new MediaStream(tracks);
							logger.debug('Got local stream from connection.getSenders()');

							// Update the Redux store with this stream for future use
							store.dispatch(stateActions.addLocalStream({
								sipSession: session,
								localStream
							}));
						}
					}
				}
			}

			// Also try other potential locations where the stream might be stored
			if (!localStream && session._localMediaStream) {
				localStream = session._localMediaStream;
				logger.debug('Got local stream from session._localMediaStream');

				// Update the Redux store with this stream
				store.dispatch(stateActions.addLocalStream({
					sipSession: session,
					localStream
				}));
			} else if (!localStream && session.localMediaStream) {
				localStream = session.localMediaStream;
				logger.debug('Got local stream from session.localMediaStream');

				// Update the Redux store with this stream
				store.dispatch(stateActions.addLocalStream({
					sipSession: session,
					localStream
				}));
			}

			// If still no stream found, attempt to create a new one as a last resort
			if (!localStream) {
				logger.warn('No local stream found, attempting to capture a new one');

				// Create a new stream as a last resort
				this._captureLocalStreamDirectly(session);

				// Let the user know what's happening
				store.dispatch(requestActions.notify({
					type: 'info',
					text: `Capturing new ${type} stream...`,
					timeout: 3000
				}));

				// Since we'll create a stream asynchronously, we need to dispatch the UI update now
				if (type === 'audio') {
					store.dispatch(stateActions.toggleAudio());
				} else if (type === 'video') {
					store.dispatch(stateActions.toggleVideo());
				}

				return;
			}

			if (type === 'video') {
				let success = false;
			  
				// IMPORTANT: We manage video by replacing tracks (camera <-> avatar canvas)
				// Do NOT call session.mute/unmute for video or it will override replaceTrack()
				if (shouldMute) {
				  success = await this._disableLocalVideo(session, localStream);
				} else {
				  success = await this._enableLocalVideo(session, localStream);
				}
			  
				if (success) {
				  store.dispatch(stateActions.toggleVideo());
				  logger.debug('Local video toggle completed successfully');
				}
			  
				return success;
			  }
			  

			if (shouldMute) {
				session.mute({ [type]: true });
			} else {
				session.unmute({ [type]: true });
			}

			if (type === 'audio') {
				store.dispatch(stateActions.toggleAudio());
			}

			logger.debug(`Local ${type} mute/unmute successfully`);
			return true;
		} catch (error) {
			logger.error(`Failed to toggle local ${type}:`, error);

			// Notify user
			store.dispatch(requestActions.notify({
				type: 'error',
				text: `Failed to toggle ${type} mute/unmute error`,
				timeout: 3000
			}));

			return false;
		}
	}

	setAudioOutput(deviceId = 'default') {
		return setAudioOutputDevice(deviceId);
	}

	// Add a method to diagnose and report connectivity issues
	async checkNetworkConnectivity() {
		logger.debug('checkNetworkConnectivity()');

		try {
			// Get current SIP configuration
			const { uri, wsServers } = store.getState().user;
			const sipConfig = { uri: wsServers || uri };

			// Run connectivity checks
			const diagnostics = await checkConnectivity(sipConfig);

			// Log the results
			logger.debug('Network diagnostics:', diagnostics);

			// If there are issues, notify the user
			if (!diagnostics.success) {
				store.dispatch(requestActions.notify({
					type: 'error',
					text: diagnostics.recommendation || 'Network connectivity issues detected',
					timeout: 8000
				}));
			}

			return diagnostics;
		} catch (error) {
			logger.error('Network diagnostics failed:', error);
			return {
				success: false,
				issues: ['Error running diagnostics'],
				recommendation: `Failed to check network connectivity: ${error.message}`
			};
		}
	}

	// Helper method to capture local stream directly from getUserMedia
	_captureLocalStreamDirectly(session) {
		// Only attempt this if the session is still active
		// Only request audio - video will be enabled when user clicks camera button
		if (session && session.sessionState !== sessionStates.TERMINATED) {
			console.log('Attempting direct media capture (audio only)');

			navigator.mediaDevices.getUserMedia({ audio: true, video: false })
				.then(directStream => {
					console.log('Got direct media stream with tracks:', {
						video: directStream.getVideoTracks().length,
						audio: directStream.getAudioTracks().length
					});

					// Dispatch the direct stream to the store only if session is still active
					if (session.sessionState !== sessionStates.TERMINATED) {
						store.dispatch(stateActions.addLocalStream({
							sipSession: session,
							localStream: directStream
						}));
						this._applySavedMuteState(session, directStream);
					} else {
						// Clean up the stream if session terminated
						directStream.getTracks().forEach(track => track.stop());
						console.log('Session terminated before stream could be added, cleaned up tracks');
					}
				})
				.catch(err => {
					console.error('Failed to get direct media stream:', err);
					store.dispatch(requestActions.notify({
						type: 'error',
						text: 'Failed to access camera or microphone. Please check your permissions.',
						timeout: 5000
					}));
				});
		} else {
			console.log('Not capturing direct stream - session already terminated');
		}
	}

	// Handler for when local tracks are detected
	_handleLocalTrackAdded(session, track) {
		if (!session || !track) return;

		try {
			// First check if we already have a local stream in Redux
			const callId = session.callId;
			if (!callId) {
				console.warn('No callId found in session');
				return;
			}

			let existingStream = null;
			try {
				existingStream = store.getState().sessions[callId].localStream;
			} catch (e) {
				console.warn('Could not access existing stream from Redux');
			}

			if (existingStream) {
				// If we already have a stream, just log
				console.log('Local track detected but stream already exists in Redux');
				return;
			}

			// Create a new MediaStream with this track
			console.log(`Creating new MediaStream from detected ${track.kind} track`);
			const newStream = new MediaStream([track]);

			// Check if track should be enabled or disabled based on current mute state
			if (track.kind === 'audio') {
				try {
					const user = store.getState().user;
					const isAudioEnabled = user.audioEnabled;
					track.enabled = isAudioEnabled;
				} catch (e) {
					console.warn('Error checking audio mute state:', e);
				}
			} else if (track.kind === 'video') {
			// Check videoEnabled state from Redux to determine if track should be enabled
			try {
				const user = store.getState().user;
				const isVideoEnabled = user.videoEnabled;
				track.enabled = isVideoEnabled;
				console.log(`Video track ${isVideoEnabled ? 'enabled' : 'disabled'} on track added (videoEnabled=${isVideoEnabled}):`, track.id);
			} catch (e) {
				console.warn('Error checking video enabled state:', e);
				// Default to disabled if we can't check state
				track.enabled = false;
				console.log('Video track disabled by default (error checking state):', track.id);
			    }
			}

			// Add the stream to Redux
			store.dispatch(stateActions.addLocalStream({
				sipSession: session,
				localStream: newStream
			}));
			this._applySavedMuteState(session, newStream);

			// Video will only be added when user explicitly clicks the camera button
			// Do not automatically add video tracks here
		} catch (error) {
			console.error('Error handling local track:', error);
		}
	}

	_collectSessionStreams(session, providedStream) {
		return collectSessionStreams(session, providedStream, this.localStream);
	}

	_collectTracks(streams, kind) {
		return collectTracks(streams, kind);
	}

	_removeTracksFromStreams(tracks, streams) {
		return removeTracksFromStreams(tracks, streams);
	}

	_buildAudioOnlyStream(streams) {
		return buildAudioOnlyStream(streams);
	}

	_releaseStream(stream) {
		return releaseStream(stream);
	}

	_cleanupVideoSenders(session) {
		if (!session || !session.connection || typeof session.connection.getSenders !== 'function') {
			return;
		}

		const pc = session.connection;
		try {
			const senders = pc.getSenders();
			senders.forEach(sender => {
				if (sender && sender.track && sender.track.kind === 'video') {
					// Replace track with null to stop sending video, but keep sender alive
					// This allows us to restore video later by replacing with a new track
					try {
						sender.replaceTrack(null).catch(err => {
							logger.warn('Failed to replace video sender track with null:', err);
						});
					} catch (error) {
						logger.warn('Failed to replace video sender track:', error);
					}
				}
			});
		} catch (error) {
			logger.warn('Error cleaning up video senders:', error);
		}
	}

	_stopAllCameraResources(session) {
		try {
			// Stop any camera streams created in _enableLocalVideo
			if (this._videoStreamsByTrackId && this._videoStreamsByTrackId.size > 0) {
				for (const [, stream] of this._videoStreamsByTrackId.entries()) {
					try { this._releaseStream(stream); } catch (_) {}
				}
				this._videoStreamsByTrackId.clear();
			}
	
			// Stop local streams from all possible places (race-condition safe)
			const candidates = [];
	
			// Redux session localStream
			try {
				const callId = session?.callId;
				const sessionData = callId ? store.getState().sessions?.[callId] : null;
				if (sessionData?.localStream) candidates.push(sessionData.localStream);
			} catch (_) {}
	
			// JsSIP session properties
			if (session?._localMediaStream) candidates.push(session._localMediaStream);
			if (session?.localMediaStream) candidates.push(session.localMediaStream);
	
			// PeerConnection local streams
			const pc = session?.connection || session?._connection;
			if (pc && typeof pc.getLocalStreams === 'function') {
				try { candidates.push(...pc.getLocalStreams()); } catch (_) {}
			}
	
			// Stop tracks on all candidates
			const seen = new Set();
			for (const s of candidates.filter(Boolean)) {
				const key = s.id || s;
				if (seen.has(key)) continue;
				seen.add(key);
				try { this._releaseStream(s); } catch (_) {}
			}
	
			// Stop avatar render loop too
			if (this._avatarRenderIntervalRef && this._avatarRenderIntervalRef.current) {
				clearInterval(this._avatarRenderIntervalRef.current);
				this._avatarRenderIntervalRef.current = null;
			}
			if (this._avatarCanvasRef) {
				this._avatarCanvasRef.current = null;
			}
		} catch (e) {
			console.warn('stop camera cleanup failed:', e);
		}
	}

	/**
	 * Create a canvas-based video track with avatar placeholder
	 * This keeps the video stream active but shows avatar instead of camera
	 */
	async _createAvatarVideoTrack(width = 1280, height = 720) {
		// Get avatar URI from store
		const getAvatarUri = () => {
			try {
				return store.getState()?.user?.avatarImageUri;
			} catch {
				return null;
			}
		};

		return createAvatarVideoTrack(
			width,
			height,
			this._avatarCanvasRef,
			this._avatarRenderIntervalRef,
			getAvatarUri
		);
	}

	async _disableLocalVideo(session, providedStream) {
		try {
			// Clear camera loading state when disabling video
			store.dispatch(stateActions.setCameraLoading({ cameraLoading: false }));
			
			const streams = this._collectSessionStreams(session, providedStream);
			const allVideoTracks = this._collectTracks(streams, 'video');

			// Filter out avatar tracks - only process camera tracks
			const cameraTracks = allVideoTracks.filter(track => {
				// Avatar tracks are created from canvas.captureStream()
				if (this._avatarCanvasRef && this._avatarCanvasRef.current) {
					try {
						const canvasStream = this._avatarCanvasRef.current.captureStream();
						const canvasTracks = canvasStream.getVideoTracks();
						// If this track ID matches a canvas track, it's an avatar track
						if (canvasTracks.some(canvasTrack => canvasTrack.id === track.id)) {
							return false; // This is an avatar track, not a camera track
						}
					} catch (e) {
						// If we can't check, assume it's a camera track
					}
				}
				// Also check if track is already ended or stopped
				if (track.readyState === 'ended') {
					return false;
				}
				return true; // This is likely a camera track
			});

			if (cameraTracks.length === 0) {
				logger.debug('No camera video tracks to disable (only avatar tracks or no tracks)');
				// If we already have avatar tracks, we're done
				if (allVideoTracks.length > 0) {
					return true;
				}
			}

			// Get video track dimensions before stopping (for avatar track)
			let videoWidth = 640;
			let videoHeight = 480;
			if (cameraTracks.length > 0 && cameraTracks[0].getSettings) {
				const settings = cameraTracks[0].getSettings();
				videoWidth = settings.width || 640;
				videoHeight = settings.height || 480;
			}

			// Create avatar video track before stopping camera
			let avatarVideoTrack = null;
			try {
				console.log('[DISABLE VIDEO] Creating avatar track before stopping camera...');
				avatarVideoTrack = await this._createAvatarVideoTrack(videoWidth, videoHeight);
				console.log('[DISABLE VIDEO] ✅ Avatar video track created:', {
					id: avatarVideoTrack.id,
					label: avatarVideoTrack.label,
					enabled: avatarVideoTrack.enabled,
					readyState: avatarVideoTrack.readyState
				});
				logger.debug('Avatar video track created for stream replacement');
			} catch (error) {
				console.error('[DISABLE VIDEO] ❌ Failed to create avatar video track:', error);
				logger.warn('Failed to create avatar video track, will remove video instead:', error);
			}

			// Stop camera tracks to turn off camera light (don't stop avatar tracks)
			cameraTracks.forEach(track => {
				try {
					track.stop();
					logger.debug('Camera track stopped (camera light off):', track.id);
					
					// Clean up stored video stream reference
					const originStream = this._videoStreamsByTrackId.get(track.id);
					if (originStream) {
						this._releaseStream(originStream);
						this._videoStreamsByTrackId.delete(track.id);
					}
				} catch (error) {
					logger.warn('Failed to stop camera track:', error);
				}
			});

			// Replace video tracks with avatar track in streams
			if (avatarVideoTrack) {
				// Remove old camera video tracks and add avatar track
				streams.forEach(stream => {
					if (!stream) return;
					
					// Remove only camera video tracks (keep existing avatar tracks if any)
					cameraTracks.forEach(track => {
						try {
							if (stream.removeTrack) {
								stream.removeTrack(track);
							}
						} catch (error) {
							// Ignore errors if track not in stream
						}
					});
					
					// Also remove any existing avatar tracks before adding the new one
					const existingVideoTracks = stream.getVideoTracks();
					existingVideoTracks.forEach(track => {
						// Remove if it's not a camera track (i.e., it's an old avatar track)
						if (!cameraTracks.some(ct => ct.id === track.id)) {
							try {
								if (stream.removeTrack) {
									stream.removeTrack(track);
									console.log('[DISABLE VIDEO] Removed old avatar track from stream:', track.id);
								}
							} catch (error) {
								// Ignore errors
							}
						}
					});

					// Add avatar track to stream only if it's not already in the stream
					const hasAvatarTrack = stream.getVideoTracks().some(track => track.id === avatarVideoTrack.id);
					if (!hasAvatarTrack) {
					try {
						console.log('[DISABLE VIDEO] Adding avatar track to stream:', {
							streamId: stream.id,
							trackId: avatarVideoTrack.id,
							streamTracksBefore: stream.getTracks().length
						});
						stream.addTrack(avatarVideoTrack);
						console.log('[DISABLE VIDEO] ✅ Avatar track added to stream, total tracks:', stream.getTracks().length);
						logger.debug('Avatar video track added to stream');
					} catch (error) {
						console.error('[DISABLE VIDEO] ❌ Failed to add avatar track to stream:', error);
						logger.warn('Failed to add avatar track to stream:', error);
						}
					} else {
						console.log('[DISABLE VIDEO] Avatar track already exists in stream, skipping add');
					}
				});

				// Replace video tracks in peer connection senders
				if (session && session.connection) {
					const pc = session.connection;
					console.log('[DISABLE VIDEO] Replacing video tracks in peer connection senders...');
					if (typeof pc.getSenders === 'function') {
						try {
							const senders = pc.getSenders();
							console.log('[DISABLE VIDEO] Found', senders.length, 'senders in peer connection');
							
							let replaced = false;
							for (const sender of senders) {
								if (sender && sender.track && sender.track.kind === 'video') {
									try {
										console.log('[DISABLE VIDEO] Replacing video track in sender:', {
											senderId: sender.id || 'unknown',
											oldTrackId: sender.track.id,
											oldTrackLabel: sender.track.label,
											newTrackId: avatarVideoTrack.id,
											newTrackLabel: avatarVideoTrack.label
										});
										await sender.replaceTrack(avatarVideoTrack);
										console.log('[DISABLE VIDEO] ✅ Successfully replaced camera track with avatar track in sender');
										logger.debug('Replaced camera track with avatar track in sender');
										replaced = true;
										break;
									} catch (error) {
										console.error('[DISABLE VIDEO] ❌ Failed to replace track in sender:', error);
										logger.warn('Failed to replace track in sender:', error);
									}
								} else if (sender && sender.track === null) {
									// Sender with null track (from previous disable)
									try {
										console.log('[DISABLE VIDEO] Replacing null track in sender with avatar track');
										await sender.replaceTrack(avatarVideoTrack);
										console.log('[DISABLE VIDEO] ✅ Successfully replaced null track with avatar track');
										replaced = true;
										break;
									} catch (error) {
										console.error('[DISABLE VIDEO] ❌ Failed to replace null track:', error);
									}
								}
							}
							
							if (!replaced) {
								console.warn('[DISABLE VIDEO] ⚠️ No video sender found to replace track');
								// Try adding track if no sender found
								if (typeof pc.addTrack === 'function') {
									try {
										console.log('[DISABLE VIDEO] Adding avatar track to peer connection');
										pc.addTrack(avatarVideoTrack, streams[0] || new MediaStream());

										console.log('[DISABLE VIDEO] ✅ Added avatar track to peer connection');
									} catch (error) {
										console.error('[DISABLE VIDEO] ❌ Failed to add avatar track:', error);
									}
								}
							}
						} catch (error) {
							console.error('[DISABLE VIDEO] ❌ Error replacing tracks in senders:', error);
							logger.warn('Error replacing tracks in senders:', error);
						}
					} else {
						console.warn('[DISABLE VIDEO] ⚠️ Peer connection does not support getSenders()');
					}
				} else {
					console.warn('[DISABLE VIDEO] ⚠️ No session or peer connection available');
				}

				// Build stream with audio + avatar video
				const audioTracks = this._collectTracks(streams, 'audio').filter(track => track.readyState !== 'ended');
				console.log('[DISABLE VIDEO] Building stream with avatar:', {
					audioTracksCount: audioTracks.length,
					avatarTrackId: avatarVideoTrack.id
				});
				const streamWithAvatar = new MediaStream();
				audioTracks.forEach(track => {
					try {
						streamWithAvatar.addTrack(track);
						console.log('[DISABLE VIDEO] Added audio track to stream:', track.id);
					} catch (error) {
						console.error('[DISABLE VIDEO] Failed to add audio track:', error);
						logger.warn('Failed to add audio track:', error);
					}
				});
				streamWithAvatar.addTrack(avatarVideoTrack);
				console.log('[DISABLE VIDEO] ✅ Stream with avatar created:', {
					streamId: streamWithAvatar.id,
					totalTracks: streamWithAvatar.getTracks().length,
					audioTracks: streamWithAvatar.getAudioTracks().length,
					videoTracks: streamWithAvatar.getVideoTracks().length,
					videoTrackIds: streamWithAvatar.getVideoTracks().map(t => t.id)
				});

				if (session) {
					session._localMediaStream = streamWithAvatar;
					session.localMediaStream = streamWithAvatar;

					store.dispatch(stateActions.addLocalStream({
						sipSession: session,
						localStream: streamWithAvatar
					}));
				}

				this.setLocalStream(streamWithAvatar);
				this.updateLocalVideoDisplay(null);

				console.log('[DISABLE VIDEO] ✅ Video disabled successfully:', {
					streamTracks: streamWithAvatar.getTracks().length,
					audioTracks: streamWithAvatar.getAudioTracks().length,
					videoTracks: streamWithAvatar.getVideoTracks().length,
					avatarTrackId: avatarVideoTrack.id,
					avatarTrackLabel: avatarVideoTrack.label
				});
				logger.debug('Video disabled: Camera light off, avatar track in stream, call continues');
				return true;
			} else {
				// Fallback: Remove only camera video tracks (keep avatar tracks if any)
				this._removeTracksFromStreams(cameraTracks, streams);
				this._cleanupVideoSenders(session);

				const audioStream = this._buildAudioOnlyStream(streams);

				if (session) {
					session._localMediaStream = audioStream;
					session.localMediaStream = audioStream;

					if (audioStream) {
						store.dispatch(stateActions.addLocalStream({
							sipSession: session,
							localStream: audioStream
						}));
					} else {
						store.dispatch(stateActions.addLocalStream({
							sipSession: session,
							localStream: null
						}));
					}
				}

				this.setLocalStream(audioStream);
				this.updateLocalVideoDisplay(null);

				logger.debug('Video disabled: Camera light off, video removed, call continues');
				return true;
			}
		} catch (error) {
			logger.error('Failed to disable local video:', error);
			store.dispatch(requestActions.notify({
				type: 'error',
				text: 'Failed to disable camera.'
			}));
			return false;
		}
	}

	async _enableLocalVideo(session, providedStream) {
		let videoStream = null;
		let videoTrack = null;

		try {
			// Set camera loading state to true when starting to enable video
			store.dispatch(stateActions.setCameraLoading({ cameraLoading: true }));
			console.log('[ENABLE VIDEO] Camera loading started');

			const streams = this._collectSessionStreams(session, providedStream);
			let targetStream = streams.find(stream => {
				try {
					return stream.getAudioTracks().length > 0;
				} catch {
					return false;
				}
			}) || streams[0] || new MediaStream();

			// Always create a new video stream when enabling (old tracks were stopped)
			// This ensures camera light turns on and new stream is ready
			console.log('Creating new video stream for camera enable');
			videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
			const videoTracks = videoStream.getVideoTracks();
			if (!videoTracks || videoTracks.length === 0) {
				throw new Error('No video tracks returned from getUserMedia');
			}

			videoTrack = videoTracks[0];
			this._videoStreamsByTrackId.set(videoTrack.id, videoStream);

			// Ensure audio tracks are in the target stream
			const audioTracks = this._collectTracks(streams, 'audio');
			audioTracks.forEach(track => {
				if (!targetStream.getTracks().some(existing => existing.id === track.id)) {
					try {
						targetStream.addTrack(track);
					} catch (error) {
						logger.warn('Failed to add existing audio track to stream:', error);
					}
				}
			});

			// Remove any stale/stopped video tracks from the stream (including avatar tracks)
			targetStream.getVideoTracks().forEach(track => {
				if (track.id !== videoTrack.id || track.readyState === 'ended') {
					try {
						// Stop avatar tracks if they exist
						if (this._avatarCanvasRef && this._avatarCanvasRef.current && track.readyState === 'live') {
							track.stop();
							logger.debug('Stopped avatar video track');
						}
						targetStream.removeTrack(track);
					} catch (error) {
						logger.warn('Failed to remove stale video track:', error);
					}
				}
			});

			// Clean up avatar canvas if it exists
			if (this._avatarCanvasRef && this._avatarCanvasRef.current) {
				try {
					// Stop any streams from the canvas
					const canvas = this._avatarCanvasRef.current;
					if (canvas.captureStream) {
						const canvasStream = canvas.captureStream();
						canvasStream.getTracks().forEach(track => track.stop());
					}
					this._avatarCanvasRef.current = null;
					logger.debug('Avatar canvas cleaned up');
				} catch (error) {
					logger.warn('Failed to cleanup avatar canvas:', error);
				}
			}
			
			// Also clear the render interval reference when cleaning up
			if (this._avatarRenderIntervalRef && this._avatarRenderIntervalRef.current) {
				try {
					clearInterval(this._avatarRenderIntervalRef.current);
					this._avatarRenderIntervalRef.current = null;
					logger.debug('Avatar render interval cleaned up');
				} catch (error) {
					logger.warn('Failed to cleanup avatar render interval:', error);
				}
			}

			// Add the new video track to the stream
			try {
				targetStream.addTrack(videoTrack);
				console.log('New video track added to stream, camera light should be on');
			} catch (error) {
				logger.warn('Video track already attached to target stream:', error);
			}

			if (session) {
				const pc = session.connection;

				if (pc) {
					let trackAttached = false;

					if (typeof pc.getSenders === 'function') {
						try {
							const senders = pc.getSenders();
							for (const sender of senders) {
								// Check for video sender (either has video track or had video track replaced with null)
								if (sender) {
									if (sender.track && sender.track.kind === 'video') {
										// Sender has active video track - replace it
										await sender.replaceTrack(videoTrack);
										trackAttached = true;
										console.log('Replaced active video track in existing sender');
										break;
									} else if (sender.track === null) {
										// Sender had track replaced with null (from disable) - restore video track
										try {
											await sender.replaceTrack(videoTrack);
											trackAttached = true;
											console.log('Restored video track to sender that had null track');
											break;
										} catch (err) {
											console.warn('Failed to restore track to null sender:', err);
											// Continue searching
										}
									}
								}
							}
						} catch (error) {
							logger.warn('Failed to replace track on existing sender:', error);
						}
					}

					if (!trackAttached && typeof pc.addTrack === 'function') {
						try {
							pc.addTrack(videoTrack, targetStream);
							trackAttached = true;
						} catch (error) {
							logger.warn('Failed to add video track to RTCPeerConnection:', error);
						}
					}

					if (!trackAttached && typeof pc.addStream === 'function') {
						try {
							pc.addStream(targetStream);
							trackAttached = true;
						} catch (error) {
							logger.warn('Failed to add stream to RTCPeerConnection:', error);
						}
					}

					if (!trackAttached) {
						throw new Error('Unable to attach video track to the active call');
					}
				}

				session._localMediaStream = targetStream;
				session.localMediaStream = targetStream;
				store.dispatch(stateActions.addLocalStream({
					sipSession: session,
					localStream: targetStream
				}));
			}

			this.setLocalStream(targetStream);
			this.updateLocalVideoDisplay(targetStream);

			// Wait for video track to be ready before clearing loading state
			// Check if track is live and enabled
			if (videoTrack) {
				const checkVideoReady = () => {
					if (videoTrack.readyState === 'live' && videoTrack.enabled) {
						store.dispatch(stateActions.setCameraLoading({ cameraLoading: false }));
						console.log('[ENABLE VIDEO] Camera loading completed - video is ready');
					} else {
						// Check again after a short delay
						setTimeout(checkVideoReady, 100);
					}
				};

				// Start checking immediately
				if (videoTrack.readyState === 'live' && videoTrack.enabled) {
					store.dispatch(stateActions.setCameraLoading({ cameraLoading: false }));
					console.log('[ENABLE VIDEO] Camera loading completed - video is ready immediately');
				} else {
					// Wait for track to become ready
					videoTrack.addEventListener('started', () => {
						store.dispatch(stateActions.setCameraLoading({ cameraLoading: false }));
						console.log('[ENABLE VIDEO] Camera loading completed - video track started');
					});

					// Fallback: check after a delay
					setTimeout(() => {
						if (videoTrack.readyState === 'live') {
							store.dispatch(stateActions.setCameraLoading({ cameraLoading: false }));
							console.log('[ENABLE VIDEO] Camera loading completed - video ready after delay');
						}
					}, 500);
				}

				// Call the checkVideoReady function if track isn't ready yet
				checkVideoReady();
			} else {
				// No video track, clear loading state
				store.dispatch(stateActions.setCameraLoading({ cameraLoading: false }));
			}

			return true;

		} catch (error) {
			logger.error('Failed to enable local video:', error);
			// Clear loading state on error
			store.dispatch(stateActions.setCameraLoading({ cameraLoading: false }));
			// If call ended mid-enable, ensure camera is stopped
            this._stopAllCameraResources(session);
			if (videoStream) {
				this._releaseStream(videoStream);
			}
			store.dispatch(requestActions.notify({
				type: 'error',
				text: 'Failed to enable camera. Please check permissions and try again.'
			}));
			return false;
		}
	}

	setLocalStream(stream) {
		console.log('SipCaller: Setting local stream', stream);
		this.localStream = stream;
		if (this.currentCall) {
			this.attachLocalStreamToCall(stream);
		}

		this.updateLocalVideoDisplay(stream);
		// this.emit('localStreamChanged', stream);
	}

	updateMediaCapabilities(hasAudio, hasVideo) {
		console.log('SipCaller: Updating media capabilities', { hasAudio, hasVideo });
		this.hasAudio = hasAudio;
		this.hasVideo = hasVideo;

		this.updateCallConstraints();

		if (this.currentCall) {
			this.updateActiveCallMedia(hasAudio, hasVideo);
		}
		// this.emit('mediaCapabilitiesChanged', { hasAudio, hasVideo });
	}

	getLocalStream() {
		return this.localStream;
	}
	getMediaCapabilities() {
		return {
			hasAudio: this.hasAudio,
			hasVideo: this.hasVideo
		};
	}
	updateLocalVideoDisplay(stream) {
		const localVideo = document.getElementById('localVideo');
		if (!localVideo) return;

		if (stream && typeof stream.getVideoTracks === 'function' && stream.getVideoTracks().length > 0) {
			localVideo.srcObject = stream;
		} else {
			localVideo.srcObject = null;
		}
	}

	updateCallConstraints() {
		this.defaultConstraints = {
			audio: this.hasAudio,
			video: this.hasVideo
		};
	}

	updateActiveCallMedia(hasAudio, hasVideo) {
		if (this.currentCall && this.localStream) {
			const audioTracks = this.localStream.getAudioTracks();
			const videoTracks = this.localStream.getVideoTracks();

			audioTracks.forEach(track => {
				track.enabled = hasAudio;
			});

			videoTracks.forEach(track => {
				track.enabled = hasVideo;
			});
		}
	}
}
